import type { ReactNode } from 'react';
import {
  Activity,
  BookOpenText,
  Settings,
  UsersRound,
  ArrowLeftRight,
  BadgeCheck,
  Building2,
  Cookie,
  Landmark,
  PiggyBank,
  Dices,
  HeartPulse,
  GraduationCap,
  BrushCleaning,
  CarFront,
  Heart,
  Flag,
  Wrench,
  Newspaper,
  House,
  Megaphone,
  Presentation,
  History,
  LayoutDashboard,
  MapPin,
  Bus,
  UtensilsCrossed,
  DoorOpen,
  TimerReset,
  Flame,
  Plane,
  TrainFront,
  TrainFrontTunnel,
  Store,
  MessageSquareWarning,
  PoundSterling,
  ShieldCheck,
  UserCog,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react';
import { groupHome, type Can, type NavGroup, type NavItem } from '@london-life/ui/admin/types';
import { SectionHub } from '@london-life/ui/admin/section-hub';
import { NavLink } from 'react-router';
import { useAdmin } from './lib/admin-context';
import { OverviewPage } from './pages/overview';
import { AdminsPage } from './pages/admins';
import { RolesPage } from './pages/roles';
import { AuditPage } from './pages/audit';
import { AccountPage } from './pages/account';
import { CompanyPage } from './pages/company';
import { WorldPage } from './pages/world';
import { AdminSessionsPage } from './pages/admin-sessions';
import { CookiesPage } from './pages/cookies';
import { StatsPage } from './pages/stats';
import { PlayersPage } from './pages/players';
import { ChatReportsPage } from './pages/chat-reports';
import { TeamChatPage } from './pages/team-chat';
import { AdvertsPage } from './pages/adverts';
import { BusinessesPage } from './pages/content/businesses';
import { AirportPage } from './pages/content/airport';
import { TrainsPage } from './pages/content/trains';
import { RailCoPage } from './pages/content/railco';
import { BankPage } from './pages/content/bank';
import { HousesPage } from './pages/content/houses';
import { BetsPage } from './pages/content/bets';
import { HealthPage } from './pages/content/health';
import { CoursesPage } from './pages/content/courses';
import { StaffPage } from './pages/content/staff';
import { CarsPage } from './pages/content/cars';
import { FamilyPage } from './pages/content/family';
import { HustlePage } from './pages/content/hustle';
import { HustleReportsPage } from './pages/hustle-reports';
import { GistPage } from './pages/content/gist';
import { GistReportsPage } from './pages/gist-reports';
import { GovPage } from './pages/content/gov';
import { GovTeamPage } from './pages/gov-team';
import { MoneyOverviewPage } from './pages/money/overview';
import { PlayerMoneyPage } from './pages/money/players';
import { ApprovalsPage } from './pages/money/approvals';
import { TransfersPage } from './pages/money/transfers';
import { PlacesPage } from './pages/content/places';
import { TravelPage } from './pages/content/travel';
import { MealsPage } from './pages/content/meals';
import { VisitsPage } from './pages/content/visits';
import { StreaksPage } from './pages/content/streaks';

// every admin page, declared once: the menu, the routes, the breadcrumbs and the group pages all read this
// list. Seven groups at the top; the pages under each show in the menu when the group is open, and as cards on
// the group's page. A page is added here when its feature is built (see docs/ADMIN.md for the full map).
type Page = NavItem & { element: ReactNode };
type Group = Omit<NavGroup, 'children'> & { element?: ReactNode; children?: Page[] };

// a group's own page when it has none: its pages as cards
function Hub({ group }: { group: Group }) {
  const { can } = useAdmin();
  return <SectionHub group={group} can={can} Link={NavLink} />;
}

const CITY = 'The city';
const HOME = 'Home life';
const WORK = 'Work and money';

export const GROUPS: Group[] = [
  {
    label: 'Overview',
    path: '/',
    icon: LayoutDashboard,
    element: <OverviewPage />,
    description: 'Where things stand',
  },
  {
    label: 'Money',
    path: '/money',
    icon: PoundSterling,
    permission: 'economy.read',
    element: <MoneyOverviewPage />,
    description: 'The books, the system accounts and the economy settings',
    children: [
      {
        label: 'Player money',
        path: '/money/players',
        icon: Wallet,
        permission: 'economy.read',
        element: <PlayerMoneyPage />,
        description: "Players' wallets and savings: look up, correct, reverse",
      },
      {
        label: 'Approvals',
        path: '/money/approvals',
        icon: BadgeCheck,
        permission: 'economy.read',
        element: <ApprovalsPage />,
        description: 'Corrections and reversals waiting for a second admin',
      },
      {
        label: 'Transfers',
        path: '/money/transfers',
        icon: ArrowLeftRight,
        permission: 'economy.read',
        element: <TransfersPage />,
        description: 'Large transfers between players held for review',
      },
    ],
  },
  {
    label: 'Game content',
    path: '/content',
    icon: BookOpenText,
    permission: 'content.read',
    description: 'Everything players see and do, as a draft the team publishes: the city, home life, work and money',
    children: [
      {
        label: 'Places',
        path: '/content/places',
        icon: MapPin,
        permission: 'content.read',
        element: <PlacesPage />,
        description: 'Places, what is inside and what players can do there',
        group: CITY,
      },
      {
        label: 'Travel',
        path: '/content/travel',
        icon: Bus,
        permission: 'content.read',
        element: <TravelPage />,
        description: 'Walking, buses, the DLR, the tube and cabs: fares and journey times',
        group: CITY,
      },
      {
        label: 'Trains',
        path: '/content/trains',
        icon: TrainFront,
        permission: 'content.read',
        element: <TrainsPage />,
        description: "King's Cross: the lines, the stops and fares, the cities up the line",
        group: CITY,
      },
      {
        label: 'Airport',
        path: '/content/airport',
        icon: Plane,
        permission: 'content.read',
        element: <AirportPage />,
        description: 'The airline, the cabins, where it flies and the fares',
        group: CITY,
      },
      {
        label: 'Houses',
        path: '/content/houses',
        icon: House,
        permission: 'content.read',
        element: <HousesPage />,
        description: 'The homes players live in, move to, buy and extend',
        group: HOME,
      },
      {
        label: 'Staff',
        path: '/content/staff',
        icon: BrushCleaning,
        permission: 'content.read',
        element: <StaffPage />,
        description: 'Help for the house: the roles for hire, wages, needs and payday',
        group: HOME,
      },
      {
        label: 'Cars',
        path: '/content/cars',
        icon: CarFront,
        permission: 'content.read',
        element: <CarsPage />,
        description: 'The showroom: the models on the floor, their prices and fuel, and the rules of the road',
        group: HOME,
      },
      {
        label: 'Family',
        path: '/content/family',
        icon: Heart,
        permission: 'content.read',
        element: <FamilyPage />,
        description: 'Partners and couples: the dates, the ring, the wedding, couple goals and levels',
        group: HOME,
      },
      {
        label: 'Gist',
        path: '/content/gist',
        icon: Newspaper,
        permission: 'content.read',
        element: <GistPage />,
        description: 'The social feed: how Trending ranks posts, reports and how much a player can post',
        group: HOME,
      },
      {
        label: 'Gov',
        path: '/content/gov',
        icon: Landmark,
        permission: 'content.read',
        element: <GovPage />,
        description: "The Mayor's election: what standing costs, the Mayor's pay, who can vote and the policies",
        group: HOME,
      },
      {
        label: 'Health',
        path: '/content/health',
        icon: HeartPulse,
        permission: 'content.read',
        element: <HealthPage />,
        description: 'Sicknesses, how players fall ill and get better, and what treatment costs',
        group: HOME,
      },
      {
        label: 'Meals',
        path: '/content/meals',
        icon: UtensilsCrossed,
        permission: 'content.read',
        element: <MealsPage />,
        description: 'What players can buy each other from a chat: prices and how they fill them up',
        group: HOME,
      },
      {
        label: 'Visits',
        path: '/content/visits',
        icon: DoorOpen,
        permission: 'content.read',
        element: <VisitsPage />,
        description: "Dropping by a neighbour's home: the knock, invites, guests, and what being let in does",
        group: HOME,
      },
      {
        label: 'Streaks',
        path: '/content/streaks',
        icon: Flame,
        permission: 'content.read',
        element: <StreaksPage />,
        description: 'What two players do together each day to keep a streak going',
        group: HOME,
      },
      {
        label: 'Businesses',
        path: '/content/businesses',
        icon: Store,
        permission: 'content.read',
        element: <BusinessesPage />,
        description: 'What players can start: kinds, premises, areas, prices and the rules',
        group: WORK,
      },
      {
        label: 'Rail Co.',
        path: '/content/railco',
        icon: TrainFrontTunnel,
        permission: 'content.read',
        element: <RailCoPage />,
        description: "Players' train companies: costs, rules, trains, paint jobs and names",
        group: WORK,
      },
      {
        label: 'Bank',
        path: '/content/bank',
        icon: PiggyBank,
        permission: 'content.read',
        element: <BankPage />,
        description: 'Savings interest, bonds and their limits',
        group: WORK,
      },
      {
        label: 'Bets',
        path: '/content/bets',
        icon: Dices,
        permission: 'content.read',
        element: <BetsPage />,
        description: 'The betting app: limits, safer play, the margin and the clubs',
        group: WORK,
      },
      {
        label: 'Hustle',
        path: '/content/hustle',
        icon: Wrench,
        permission: 'content.read',
        element: <HustlePage />,
        description: 'Players hiring players: posts, offers, held fees, listings and each kind of job',
        group: WORK,
      },
      {
        label: 'Courses',
        path: '/content/courses',
        icon: GraduationCap,
        permission: 'content.read',
        element: <CoursesPage />,
        description: 'Courses, their lessons, notes and quizzes, and what certificates are worth',
        group: WORK,
      },
    ],
  },
  {
    label: 'Players',
    path: '/players',
    icon: Users,
    permission: 'players.read',
    element: <PlayersPage />,
    description: 'Who plays: accounts, lives, bans',
    children: [
      {
        label: 'Chat reports',
        path: '/players/chat-reports',
        icon: MessageSquareWarning,
        permission: 'players.read',
        element: <ChatReportsPage />,
        description: 'Messages players reported, to read and act on',
      },
      {
        label: 'Hustle reports',
        path: '/players/hustle-reports',
        icon: Flag,
        permission: 'players.read',
        element: <HustleReportsPage />,
        description: 'Hustle posts players reported, to remove or keep',
      },
      {
        label: 'Gist',
        path: '/players/gist',
        icon: Newspaper,
        permission: 'players.read',
        element: <GistReportsPage />,
        description: 'Gist posts and comments players reported, and London Gist news',
      },
      {
        label: 'Gov',
        path: '/players/gov',
        icon: Landmark,
        permission: 'players.read',
        element: <GovTeamPage />,
        description: "This week's race and count, the Mayor, their announcements and reports, past results",
      },
      {
        label: 'Adverts',
        path: '/players/adverts',
        icon: Presentation,
        permission: 'ads.read',
        element: <AdvertsPage />,
        description: 'Ads players put on the map’s boards: check, take down, prices',
      },
      {
        label: 'Team chat',
        path: '/players/team-chat',
        icon: Megaphone,
        permission: 'content.read',
        element: <TeamChatPage />,
        description: 'The team’s messages to players in the game’s chat',
      },
    ],
  },
  {
    label: 'Team',
    path: '/team',
    icon: UsersRound,
    description: 'Who runs the game, what each of them may do, and what they did',
    children: [
      {
        label: 'Admins',
        path: '/admins',
        icon: UserCog,
        permission: 'admins.manage',
        element: <AdminsPage />,
        description: 'The admins, their roles, and who is disabled',
      },
      {
        label: 'Roles',
        path: '/roles',
        icon: ShieldCheck,
        permission: 'admins.manage',
        element: <RolesPage />,
        description: 'What each role may read and change',
      },
      {
        label: 'Audit log',
        path: '/audit',
        icon: History,
        permission: 'audit.read',
        element: <AuditPage />,
        description: 'Everything admins did, with when and by whom',
      },
    ],
  },
  {
    label: 'Settings',
    path: '/settings',
    icon: Settings,
    description: 'The company, the real world the game follows, cookies, sign-in and stats',
    children: [
      {
        label: 'Company details',
        path: '/settings/company',
        icon: Building2,
        permission: 'company.manage',
        element: <CompanyPage />,
        description: 'The company behind the game, as shown on the site',
      },
      {
        label: 'Real world',
        path: '/settings/world',
        icon: Landmark,
        permission: 'world.manage',
        element: <WorldPage />,
        description: 'The real-world facts the game follows: the PM, the weather, the news',
      },
      {
        label: 'Cookies',
        path: '/settings/cookies',
        icon: Cookie,
        permission: 'company.manage',
        element: <CookiesPage />,
        description: 'The cookie banner and what each cookie is for',
      },
      {
        label: 'Admin sign-in',
        path: '/settings/sign-in',
        icon: TimerReset,
        permission: 'admins.manage',
        element: <AdminSessionsPage />,
        description: 'How long an idle admin stays signed in',
      },
      {
        label: 'Visitor stats',
        path: '/settings/stats',
        icon: Activity,
        permission: 'stats.manage',
        element: <StatsPage />,
        description: 'Visitor counting on the site, and who may see it',
      },
    ],
  },
  { label: 'My account', path: '/account', icon: UserRound, element: <AccountPage /> },
];

// every page an admin may open, for the routes: each group's own page (its hub when it has none) when the
// group opens there, and the pages under it they may see
export const pagesFor = (can: Can): Page[] =>
  GROUPS.flatMap((group) => [
    ...(groupHome(group, can) === group.path ? [{ ...group, element: group.element ?? <Hub group={group} /> }] : []),
    ...(group.children ?? []).filter((page) => !page.permission || can(page.permission)),
  ]);

// the group and page names for the current address, shown as breadcrumbs
export function crumbsFor(pathname: string) {
  for (const group of GROUPS) {
    if (group.path === pathname) return group.label;
    const page = group.children?.find((item) => item.path === pathname);
    if (page) return [group.label, page.group, page.label].filter(Boolean).join(' › ');
  }
  return '';
}
